import { useLiveQuery } from "dexie-react-hooks";
import { Check, ChevronDown, Pause, Play, RotateCcw, Settings2, SkipForward, Square, Timer, Trash2 } from "lucide-react";
import { useState } from "react";
import { toISODate } from "@todoer/shared";
import { Dialog, OptionList, Popover, usePopover } from "../components/overlay.js";
import { priorityMeta } from "../components/pickers.js";
import { Button, cx, EmptyState, Field, IconButton, Input, PageHeader, SegmentedControl, Switch } from "../components/ui.js";
import { db } from "../lib/db.js";
import {
  elapsedMs, fmtClock, nextPhase, pause, phaseMinutes, remainingMs, reset, setMode, setSettings, setTask, start, stop, useFocus,
} from "../lib/focus.js";
import { friendlyDate } from "../lib/format.js";
import { useNow } from "../lib/hooks.js";
import { cmd, dispatch } from "../lib/sync.js";

/** Pomodoro / stopwatch focus timer linked to a task, with a session log. */
export function Focus() {
  const s = useFocus();
  useNow(500);
  const today = toISODate(new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const picker = usePopover<HTMLButtonElement>();
  const tasks = useLiveQuery(() => db.tasks.filter((t) => !t.completedAt).toArray(), [], []);
  const allTasks = useLiveQuery(() => db.tasks.toArray(), [], []);
  const sessions = useLiveQuery(() => db.focusSessions.orderBy("startedAt").reverse().limit(50).toArray(), [], []);
  const task = tasks.find((t) => t.id === s.taskId);
  const pomodoro = s.mode === "pomodoro";
  const shown = pomodoro ? remainingMs(s) : elapsedMs(s);
  const total = phaseMinutes(s) * 60_000;
  const progress = pomodoro ? 1 - shown / total : Math.min(1, (shown % 3_600_000) / 3_600_000);
  const todays = sessions.filter((x) => toISODate(new Date(x.startedAt)) === today);
  const todayMin = Math.round(todays.reduce((n, x) => n + x.durationSec, 0) / 60);
  const phaseColor = s.phase === "focus" ? "var(--color-brand)" : "var(--color-success)";
  const R = 108;
  const C = 2 * Math.PI * R;
  const enableNotifications = () => { try { if (Notification.permission === "default") void Notification.requestPermission(); } catch { /* ignore */ } };
  const cycle = s.completedFocus % s.settings.longEvery;

  const groups = new Map<string, typeof sessions>();
  for (const x of sessions) { const d = toISODate(new Date(x.startedAt)); groups.set(d, [...(groups.get(d) ?? []), x]); }

  return (
    <div>
      <PageHeader title="Focus" subtitle={`${todayMin} min focused today · ${todays.length} session${todays.length === 1 ? "" : "s"}`}
        actions={pomodoro && <IconButton icon={Settings2} label="Timer settings" onClick={() => setSettingsOpen(true)} />} />
      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        <SegmentedControl label="Timer mode" value={s.mode} onChange={(m) => { if (!s.running) setMode(m); }}
          options={[{ value: "pomodoro", label: "Pomodoro", icon: Timer }, { value: "stopwatch", label: "Stopwatch", icon: RotateCcw }]} />

        <div className="relative mt-6 h-60 w-60">
          <svg viewBox="0 0 240 240" className="h-full w-full -rotate-90" aria-hidden>
            <circle cx="120" cy="120" r={R} fill="none" strokeWidth="8" stroke="var(--color-surface-sunken)" />
            <circle cx="120" cy="120" r={R} fill="none" strokeWidth="8" strokeLinecap="round" stroke={phaseColor}
              strokeDasharray={C} strokeDashoffset={C * (1 - progress)} style={{ transition: "stroke-dashoffset 500ms linear" }} />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div>
              <div className="tabular text-display font-semibold" role="timer" aria-live="off">{fmtClock(shown)}</div>
              <div className="mt-1 text-body font-medium" style={{ color: phaseColor }}>
                {pomodoro ? (s.phase === "focus" ? "Focus" : s.phase === "short" ? "Short break" : "Long break") : "Stopwatch"}
              </div>
            </div>
          </div>
        </div>

        {pomodoro && (
          <div className="mt-3 flex items-center gap-1.5" aria-label={`${cycle} of ${s.settings.longEvery} focus blocks before a long break`} role="img">
            {Array.from({ length: s.settings.longEvery }, (_, i) => (
              <span key={i} className={cx("h-2 w-2 rounded-full", i < cycle ? "bg-brand" : "bg-border-strong")} />
            ))}
          </div>
        )}

        <button type="button" onClick={picker.toggle} aria-haspopup="dialog" aria-expanded={picker.isOpen}
          className="mt-5 flex h-10 max-w-full items-center gap-2 rounded-full border border-border bg-surface-raised px-4 text-body hover:border-border-strong phone:h-11">
          <span className="text-text-muted">Working on:</span>
          <span className="min-w-0 truncate font-medium">{task?.content ?? "No task, just focus"}</span>
          <ChevronDown size={16} className="shrink-0 text-text-faint" aria-hidden />
        </button>
        {picker.anchor && (
          <Popover anchor={picker.anchor} onClose={picker.close} title="Working on" width={320} align="center">
            <OptionList label="Task" search placeholder="Find a task" options={[
              { key: "none", label: "No task, just focus", selected: !s.taskId, onSelect: () => { setTask(null); picker.close(); } },
              ...[...tasks].sort((a, b) => b.priority - a.priority || a.content.localeCompare(b.content)).map((t) => ({
                key: t.id, label: t.content, selected: t.id === s.taskId,
                leading: <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full border-[1.5px]" style={{ borderColor: t.priority > 1 ? priorityMeta(t.priority).color : "var(--color-border-strong)" }} />,
                onSelect: () => { setTask(t.id); picker.close(); },
              })),
            ]} />
          </Popover>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {s.running
            ? <Button variant="primary" size="lg" icon={Pause} onClick={pause} className="min-w-32">Pause</Button>
            : <Button variant="primary" size="lg" icon={Play} onClick={() => { enableNotifications(); start(); }} className="min-w-32">{s.elapsedMs ? "Resume" : "Start"}</Button>}
          {pomodoro ? <>
            <Button size="lg" icon={SkipForward} onClick={() => void nextPhase(false)}>Skip</Button>
            <Button size="lg" icon={RotateCcw} onClick={reset}>Reset</Button>
          </> : <Button size="lg" icon={Square} onClick={() => void stop()} disabled={!s.elapsedMs && !s.running}>Stop & save</Button>}
        </div>
        {pomodoro && s.phase === "focus" && (s.running || s.elapsedMs > 0) && <Button variant="ghost" size="sm" className="mt-2" onClick={() => void stop()}>End early & save</Button>}
        {task && <Button variant="ghost" size="sm" icon={Check} className="mt-1" onClick={() => void dispatch(cmd("item_complete", { id: task.id }))}>Mark “{task.content}” complete</Button>}
      </div>

      <section className="mx-auto mt-10 max-w-2xl" aria-labelledby="sessions-h">
        <h2 id="sessions-h" className="mb-3 text-heading font-semibold">Recent sessions</h2>
        {!sessions.length && <EmptyState compact kind="focus" title="No sessions yet">Sessions over a minute are saved automatically.</EmptyState>}
        <div className="space-y-5">
          {[...groups.entries()].map(([day, list]) => (
            <div key={day}>
              <div className="mb-1.5 flex items-baseline gap-2">
                <h3 className="text-caption font-semibold uppercase text-text-faint">{friendlyDate(day)}</h3>
                <span className="tabular text-meta text-text-faint">{Math.round(list.reduce((n, x) => n + x.durationSec, 0) / 60)} min</span>
              </div>
              <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface-raised">
                {list.map((x) => (
                  <li key={x.id} className="group flex min-h-12 items-center gap-3 px-3 py-2 text-body">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-control bg-brand-soft text-brand-text"><Timer size={16} aria-hidden /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{allTasks.find((t) => t.id === x.taskId)?.content ?? (x.taskId ? "Deleted task" : "No task")}</span>
                      <span className="tabular text-meta text-text-faint">{new Date(x.startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </span>
                    <span className="tabular font-medium">{Math.round(x.durationSec / 60)} min</span>
                    <IconButton size="sm" icon={Trash2} label="Delete session" tone="danger" className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 phone:opacity-100"
                      onClick={() => void dispatch(cmd("focus_delete", { id: x.id }))} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {settingsOpen && (
        <Dialog title="Timer settings" size="sm" onClose={() => setSettingsOpen(false)} footer={<Button variant="primary" onClick={() => setSettingsOpen(false)}>Done</Button>}>
          <div className="grid grid-cols-2 gap-4">
            {([["focusMin", "Focus (min)"], ["shortMin", "Short break (min)"], ["longMin", "Long break (min)"], ["longEvery", "Long break every"]] as const).map(([k, l]) => (
              <Field key={k} label={l} htmlFor={`fs-${k}`}>
                <Input id={`fs-${k}`} type="number" min={1} max={180} className="tabular" value={s.settings[k]} onChange={(e) => setSettings({ [k]: Math.max(1, Number(e.target.value) || 1) })} />
              </Field>
            ))}
          </div>
          <div className="mt-5"><Switch label="Start the next phase automatically" checked={s.settings.autoStart} onChange={(v) => setSettings({ autoStart: v })} /></div>
        </Dialog>
      )}
    </div>
  );
}
